import React, { useEffect, useState, useCallback, useRef, forwardRef } from 'react';
import {
  Content,
  Header,
  Table,
  TableColumn,
  Progress,
  TableProps,
} from '@backstage/core-components';
import {
  CircularProgress,
  Snackbar,
  Select,
  MenuItem,
  FormControl,
  Switch,
  Tooltip,
  Typography,
  IconButton,
  makeStyles,
  useTheme,
} from '@material-ui/core';
import debounce from 'lodash.debounce';
import CheckIcon from '@material-ui/icons/Check';
import CloseIcon from '@material-ui/icons/Close';
import SearchIcon from '@material-ui/icons/Search';
import RefreshIcon from '@material-ui/icons/Sync';
import './UsersFetchList.css';
import { alertApiRef, configApiRef, identityApiRef, microsoftAuthApiRef, useApi } from '@backstage/core-plugin-api';
import { useEpDevPNotifications } from '@solace-labs/backstage-plugin-ep-devp-react';
import { BackstageTheme } from '@backstage/theme';
import { useServiceRegistryTranslation } from '../../i18n/ServiceRegistryLanguageProvider';
import { tablePaginationLocalization } from '../../i18n/pagination';

const useStyles = makeStyles(
  theme => ({
    root: {
      "& $actions": {
        paddingRight: theme.spacing(1),
      },
      "& table $actions": {
        paddingRight: theme.spacing(0),
      },
    },
    actions: {
      display: "flex",
      justifyContent: "right"
    },
    noDataMessage: {
      padding: theme.spacing(2),
      display: "flex",
      justifyContent: "center",
    },
  }),
  { name: 'UsersFetchList' },
);

interface User {
  id: number;
  uuid: string;
  firstname: string;
  lastname: string;
  email: string;
  username: string;
  status: number;
  isactive: boolean;
  roles?: Array<{
    name: string;
    uuid: string;
  }>;
}

type TableOptions = NonNullable<TableProps<User>['options']>;

const UsersFetchList = () => {
  const { t } = useServiceRegistryTranslation();
  const config = useApi(configApiRef);
  const baseUrl = config.getString('backend.baseUrl');
  // const [originalUsers, setOriginalUsers] = useState<User[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [loadingApproval, setLoadingApproval] = useState<{
    [key: string]: string | null;
  }>({});
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [localSearchQuery, setLocalSearchQuery] = useState('');
  const createNotification = useEpDevPNotifications();
  const [accountStatusFilter, setAccountStatusFilter] = useState('all');
  const [showActiveOnly, setShowActiveOnly] = useState(true);
  const [entraUserInvitationScope, setEntraUserInvitationScope] = useState<'APPLICATION' | 'DELEGATED'>('APPLICATION');
  const searchInputRef = useRef<HTMLInputElement>(null);
  const identityApi = useApi(identityApiRef);
  const microsoftAuthApi = useApi(microsoftAuthApiRef);
  const alertApi = useApi(alertApiRef);
  const tableRef = useRef<any>();
  const classes = useStyles();
  const theme = useTheme<BackstageTheme>();

  // Add state for sorting
  const [orderBy, setOrderBy] = useState<string>('createddate');
  const [orderDirection, setOrderDirection] = useState<'asc' | 'desc'>('desc');

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const { token } = await identityApi.getCredentials();

      let url = `${baseUrl}/api/users?pageSize=${rowsPerPage}&pageNumber=${page + 1}&activeOnly=${showActiveOnly}`;

      if (searchQuery && searchQuery.trim()) {
        url += `&searchTerm=${encodeURIComponent(searchQuery)}`;
      }

      if (accountStatusFilter !== 'all') {
        url += `&accountStatus=${accountStatusFilter}`;
      }

      // Add sorting parameters
      url += `&sortFieldName=${orderBy=="roleNames"?"roles":orderBy}&sortDirection=${orderDirection}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setEntraUserInvitationScope(
        data.entraUserInvitationScope === 'DELEGATED' ? 'DELEGATED' : 'APPLICATION',
      );
      
      // Set users directly (no client-side sorting)
      const usersWithSearchableRoles = (data.users || []).map(user => ({
      ...user,
      roleNames: user.roles?.map(role => role.name).join(', ') || 'No role assigned'
    }));
      setUsers(usersWithSearchableRoles);
      
      setTotalCount(data.totalCount || 0);
    } catch (err) {
      console.error('Error fetching users:', err);
      setUsers([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [searchQuery, page, rowsPerPage, accountStatusFilter, showActiveOnly, orderBy, orderDirection]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Apply sorting whenever originalUsers, orderBy or orderDirection changes
  // useEffect(() => {
  //   if (originalUsers.length > 0) {
  //     const sortedUsers = sortUsers(originalUsers, orderBy, orderDirection);
  //     setUsers(sortedUsers);
  //   } else {
  //     setUsers([]);
  //   }
  // }, [originalUsers, orderBy, orderDirection]);

  const handleAccountStatusFilterChange = (
    event: React.ChangeEvent<{ value: unknown }>,
  ) => {
    setAccountStatusFilter(event.target.value as string);
    setPage(0);
  };

  const handleActiveToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    setShowActiveOnly(event.target.checked);
    setPage(0);
  };

  const handleApprove = async (userId: string) => {
    const { token } = await identityApi.getCredentials();
    setLoadingApproval(prev => ({ ...prev, [userId]: 'approve' }));
    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      };

      if (entraUserInvitationScope === 'DELEGATED') {
        try {
          const graphToken = await microsoftAuthApi.getAccessToken([
            'https://graph.microsoft.com/User.Invite.All',
          ]);
          if (!graphToken) {
            throw new Error('Microsoft Entra returned no access token');
          }
          headers['X-SSP-Graph-Token'] = graphToken;
        } catch (_error) {
          alertApi.post({
            message: t('users.entraUnavailable'),
            severity: 'warning',
            display: 'transient',
          });
          return;
        }
      }

      const response = await fetch(`${baseUrl}/api/users/approve/${userId}`, {
        method: 'POST',
        headers,
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        const insufficientEntraPermission = response.status === 403 &&
          result.code === 'INSUFFICIENT_ENTRA_PERMISSION';
        alertApi.post({
          message: insufficientEntraPermission
            ? t('users.entraDenied')
            : t('users.approveFailed'),
          severity: insufficientEntraPermission ? 'warning' : 'error',
          display: 'transient',
        });
        return;
      }
      // setSnackbarMessage(result.message);
      createNotification(t('users.approveSuccess'));
      // setSnackbarOpen(true);
      fetchUsers();
    } catch (error) {
      console.error('Error approving user:', error);
    } finally {
      setLoadingApproval(prev => ({ ...prev, [userId]: null }));
    }
  };

  const handleReject = async (userId: string) => {
    setLoadingApproval(prev => ({ ...prev, [userId]: 'reject' }));
    const { token } = await identityApi.getCredentials();
    try {
      const response = await fetch(`${baseUrl}/api/users/reject/${userId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        throw new Error(`Failed to reject user: ${response.statusText}`);
      }
      const result = await response.json();
      createNotification(t('users.rejectSuccess'));
      // setSnackbarMessage(result.message);
      // setSnackbarOpen(true);
      fetchUsers();
    } catch (error) {
      console.error('Error rejecting user:', error);
    } finally {
      setLoadingApproval(prev => ({ ...prev, [userId]: null }));
    }
  };

  const debouncedSearch = useCallback(
    debounce((query: string) => {
      if (query.trim()) {
        setSearchQuery(query);
        setPage(0);
      }
    }, 800),
    [],
  );

  const handleSearchChange = (query: string) => {
    setLocalSearchQuery(query); // Update local state immediately
    if (query.trim() === '') {
      // If search is cleared, reset to show all users without full re-render
      setSearchQuery('');
      setPage(0);
    } else {
      debouncedSearch(query); // Call debounced search for API
    }
  };

  useEffect(() => {
    setLocalSearchQuery(searchQuery);
  }, [searchQuery]);

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const reloadData = () => {
    tableRef.current?.onQueryChange();
  };

  // Handle sorting change
  const handleChangeSort = useCallback((columnIndex: number, direction: 'asc' | 'desc') => {
    const columnField = columns[columnIndex]?.field as string;
    console.log(`Handling sort: field=${columnField}, direction=${direction}`);

    setOrderBy(columnField);
    setOrderDirection(direction);
    setPage(0);
  }, []);

  const columns: TableColumn<User>[] = [
    {
      title: t('users.firstName'),
      field: 'firstname',
      sorting: true,
      customSort: () => 0
    },
    {
      title: t('users.lastName'),
      field: 'lastname',
      sorting: true,
      customSort: () => 0
    },
    {
      title: t('users.email'),
      field: 'email',
      sorting: true,
      customSort: () => 0
    },
    {
      title: t('users.role'),
      field: 'roleNames',
      sorting: true, // Enable sorting for role column
      customSort: () => 0,
      render: (row: Partial<User>) => {
        if (!row.roles || row.roles.length === 0) {
          return <Typography variant="body2" color="textSecondary">{t('users.noRole')}</Typography>;
        }

        // Since user only has one role, just display it
        return <Typography variant="body2">{row.roles[0].name}</Typography>;
      },
    },
    {
      title: t('users.accountStatus'),
      field: 'status',
      sorting: true,
      customSort: () => 0,
      render: (row: Partial<User>) =>
        row.status === 1
          ? t('users.approved')
          : row.status === 2
            ? t('users.rejected')
            : row.status === 3
              ? t('users.pending')
              : t('users.unknown'),
    },
    {
      title: t('users.activityStatus'),
      field: 'isactive',
      sorting: true,
      customSort: () => 0,
      render: (row: Partial<User>) =>
        row.isactive === true
          ? t('users.active')
          : row.isactive === false
            ? t('users.inactive')
            : t('users.unknown'),
    },
    {
      title: t('users.actions'),
      sorting: false,
      render: (row: Partial<User>) => (
        <div style={{ display: 'flex', flexDirection: 'row' }}>
          {row.status === 3 && (
            <>
              <Tooltip title={t('users.approve')} placement="top">
                <IconButton
                  color="primary"
                  onClick={() => handleApprove(row.uuid as string)}
                  disabled={loadingApproval[row.uuid as string] === 'approve'}
                  size="small"
                >
                  {loadingApproval[row.uuid as string] === 'approve' ? (
                    <CircularProgress size={24} />
                  ) : (
                    <CheckIcon />
                  )}
                </IconButton>
              </Tooltip>
              <Tooltip title={t('users.reject')} placement="top">
                <IconButton
                  style={{ color: '#d32f2f' }}
                  onClick={() => handleReject(row.uuid as string)}
                  disabled={loadingApproval[row.uuid as string] === 'reject'}
                  size="small"
                >
                  {loadingApproval[row.uuid as string] === 'reject' ? (
                    <CircularProgress size={24} style={{ color: '#d32f2f' }} />
                  ) : (
                    <CloseIcon />
                  )}
                </IconButton>
              </Tooltip>
            </>
          )}
        </div>
      ),
    },
  ];



  const options: TableOptions = {
    paging: true,
    pageSize: rowsPerPage,
    pageSizeOptions: [10, 20, 50, 100],
    emptyRowsWhenPaging: false,
    search: true,
    filtering: false,
    searchText: localSearchQuery,
    sorting: true,
    debounceInterval: 500,
    padding: 'dense',
    loadingType: 'linear',
    thirdSortClick: false,
    paginationType: "normal",
    // initialSorting: [{ field: 'firstname', order: 'asc' }]
  };

  if (loading && users.length === 0 && !searchQuery) {
    return <Progress />;
  }

  return (
    <>
      <Header
        title={t('users.title')}
        subtitle={t('users.subtitle')}
        style={{ margin: 0 }}
      />
      <Content>
        <div className={`article ${classes.root}`}>
          <div className="filter-class">
            <FormControl className="form-control custom-filter">
              <Typography variant="body2" style={{ marginBottom: '0.25rem' }}>
                {t('users.accountStatus')}
              </Typography>
              <Select
                value={accountStatusFilter}
                onChange={handleAccountStatusFilterChange}
                className="dropdown-items"
              >
                <MenuItem value="all">{t('users.all')}</MenuItem>
                <MenuItem value="approved">{t('users.approved')}</MenuItem>
                <MenuItem value="rejected">{t('users.rejected')}</MenuItem>
                <MenuItem value="pending">{t('users.pending')}</MenuItem>
              </Select>
            </FormControl>

            <div className="toggle-button">
              <Typography variant="body2" style={{ marginBottom: '0.25rem' }}>
                {t('users.activityStatus')}
              </Typography>
              <Tooltip
                title={showActiveOnly ? t('users.active') : t('users.inactive')}
                placement="top"
              >
                <Switch
                  checked={showActiveOnly}
                  onChange={handleActiveToggle}
                  color="primary"
                />
              </Tooltip>
            </div>
          </div>

          <Table<User>
            tableRef={tableRef}
            options={options}
            data={users}
            columns={columns}
            onPageChange={newPage => setPage(newPage)}
            onRowsPerPageChange={newRowsPerPage => {
              setRowsPerPage(newRowsPerPage);
              setPage(0);
            }}
            onSearchChange={handleSearchChange}
            page={page}
            totalCount={totalCount}
            isLoading={loading}
            style={{ width: '100%' }}
            onOrderChange={handleChangeSort}
            orderBy={columns.findIndex(col => col.field === orderBy)}
            orderDirection={orderDirection}
            actions={[
              {
                icon: RefreshIcon,
                tooltip: t('users.refresh'),
                isFreeAction: true,
                onClick: () => {
                  fetchUsers();
                },
              },
            ]}
            icons={{
              ...Table.icons,
              Search: forwardRef((iconProps, ref) => (
                <SearchIcon {...iconProps} ref={ref} />
              )),
            }}
            localization={{
              pagination: tablePaginationLocalization(t),
              toolbar: { searchPlaceholder: t('users.search'), searchTooltip: '' },
              header: { actions: '' },
              body: {
                emptyDataSourceMessage: (
                  <Typography color="textSecondary" className={classes.noDataMessage}>
                    {t('users.empty')}
                  </Typography>
                )
              },
            }}
          />
        </div>
      </Content>
    </>
  );
};

export default UsersFetchList;
